import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { notification } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import {
  AllocateProcessRiskControlRequest,
  AllocateProcessRiskRequest,
  Assignment,
  Column,
  DragItem,
  Scope,
  SeverityName
} from 'store/ducks/InternalControls/types';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import ApiService from 'utils/ApiCalls';
import { internalControlsOp } from 'store/ducks/InternalControls';
import Helpers from 'utils/Helpers';
import { ColumnView } from './ColumnView';
import * as Styled from './InternalControlsDND.styled';

interface DragAndDropBPRProps {
  backend?: any;
}

const STORAGE_KEY = 'bpr-assignments-v1';

const DragAndDropBPR = ({ backend }: DragAndDropBPRProps) => {
  const { scheme, drawTable, selectedEntityId } = useSelector(
    (state: RootState) => ({
      scheme: state.global.scheme,
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId
    })
  );

  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [initialAssignments, setInitialAssignments] = useState<Assignment[]>(
    []
  );
  const [columns, setColumns] = useState<Column[]>([]);
  const [processesFromApi, setProcessesFromApi] = useState<any[]>([]);
  const [risksFromApi, setRisksFromApi] = useState<any[]>([]);
  const query = new URLSearchParams(window.location.search);

  const scopeOfFilters = Helpers.buildScopeFromFilters(
    Object.fromEntries(query.entries())
  );

  // Auto-trigger data loading when component mounts
  useEffect(() => {
    dispatch(internalControlsOp.setDrawTable(true));
  }, [dispatch]);

  const normalizeScopeForApi = (scope: Scope) => ({
    group_id: scope.group_id ?? undefined,
    division_id: scope.division_id ?? undefined,
    region_id: scope.region_id ?? undefined,
    legal_entity_id: scope.legal_entity_id ?? undefined,
    business_unit_id: scope.business_unit_id ?? undefined
  });

  useEffect(() => {
    if (!drawTable) return;

    const fetchData = async () => {
      try {
        const [processesRes, risksRes, controlsRes] = await Promise.allSettled([
          ApiService.getBusinessProcessL4BPR(
            normalizeScopeForApi(scopeOfFilters)
          ),
          ApiService.getOrgRisk(scopeOfFilters),
          ApiService.getOrgControl(scopeOfFilters)
        ]);
        const processes =
          processesRes.status === 'fulfilled'
            ? processesRes.value.data.data ?? []
            : [];

        const risks =
          risksRes.status === 'fulfilled' ? risksRes.value.data.data ?? [] : [];

        const controls =
          controlsRes.status === 'fulfilled'
            ? controlsRes.value.data.data ?? []
            : [];

        const rawAssignments = localStorage.getItem(STORAGE_KEY);
        const persistedAssignments: Assignment[] = rawAssignments
          ? JSON.parse(rawAssignments)
          : [];

        const workingAssignments: Assignment[] = [...persistedAssignments];

        const validRiskInstanceIds = new Set<string>();
        const validControlKeys = new Set<string>();

        processes.forEach((proc: any) => {
          proc.risks?.forEach((risk: any) => {
            const riskInstanceId = `risk-${risk.id}-${proc.id}`;
            validRiskInstanceIds.add(riskInstanceId);

            if (
              !workingAssignments.some(
                (a) =>
                  a.sourceType === 'RISK' && a.riskInstanceId === riskInstanceId
              )
            ) {
              // Get severity from risk object or from risksFromApi
              let riskSeverity = risk.severity ?? null;
              if (!riskSeverity && risksFromApi.length > 0) {
                const riskFromApi = risksFromApi.find(
                  (r: any) => r.id === risk.id
                );
                riskSeverity =
                  riskFromApi?.severity ??
                  riskFromApi?.instances?.[0]?.severity ??
                  null;
              }

              workingAssignments.push({
                assignmentId: `${proc.id}-RISK-${risk.id}`,
                bprId: proc.id,
                riskInstanceId,
                sourceType: 'RISK',
                sourceId: risk.id,
                text: risk.title,
                severity: riskSeverity,
                control_status: null
              });
            }

            // Add linked controls
            risk.controls?.forEach((ctrl: any) => {
              const controlKey = `${riskInstanceId}-${ctrl.id}`;
              validControlKeys.add(controlKey);

              if (
                !workingAssignments.some(
                  (a) =>
                    a.sourceType === 'CONTROL' &&
                    a.sourceId === ctrl.id &&
                    a.riskInstanceId === riskInstanceId
                )
              ) {
                workingAssignments.push({
                  assignmentId: `${proc.id}-CONTROL-${ctrl.id}-${riskInstanceId}`,
                  bprId: proc.id,
                  riskInstanceId,
                  sourceType: 'CONTROL',
                  sourceId: ctrl.id,
                  text:
                    ctrl.control_name || ctrl.name || ctrl.description || '',
                  description: ctrl.description || '',
                  control_status: ctrl.control_status ?? null
                });
              }
            });
          });
        });

        // Clean invalid assignments
        const cleanedAssignments = workingAssignments.filter((a) => {
          if (a.sourceType === 'RISK') {
            return (
              a.riskInstanceId && validRiskInstanceIds.has(a.riskInstanceId)
            );
          }
          if (a.sourceType === 'CONTROL') {
            return (
              a.riskInstanceId &&
              validControlKeys.has(`${a.riskInstanceId}-${a.sourceId}`)
            );
          }
          return true;
        });

        // Normalize old risks: populate missing severity from API
        const normalizedAssignments = cleanedAssignments.map((a) => {
          if (a.sourceType === 'RISK' && !a.severity) {
            const proc = processes.find(
              (p: { id: number }) => p.id === a.bprId
            );
            const riskFromApi = proc?.risks?.find(
              (r: { id: number }) => r.id === a.sourceId
            );
            return {
              ...a,
              severity: riskFromApi?.severity ?? null
            };
          }
          return a;
        });

        // Set columns
        const newColumns = [
          {
            id: '1',
            title: t('BUSINESS_PROCESSES'),
            canDrop: false,
            items: [...processes]
              .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
              .map((p: any) => ({
                id: p.id,
                text: p.title,
                description: p.description || '',
                type: 'BPR' as const,
                process_type: p.process_type,
                process_criticality: p.process_criticality ?? undefined,
                risks: (p.risks || []).map((r: any) => ({
                  id: r.id,
                  text: r.title || r.text,
                  type: 'RISK',
                  severity: r.severity ?? null,
                  controls: r.controls || []
                }))
              }))
          },
          {
            id: '2',
            title: t('RISKS'),
            canDrop: false,
            items: [...risks]
              .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
              .map((r: any) => {
                // Try to get severity from multiple sources
                let severity = r.severity ?? null;
                if (!severity && r.instances && r.instances.length > 0) {
                  severity = r.instances[0].severity ?? null;
                }
                return {
                  id: r.id,
                  text: r.title,
                  type: 'OTHER' as const,
                  category: r.category,
                  notUsed: r.not_used === true,
                  severity: severity,
                  instances: r.instances ?? [],
                  description: r.description
                };
              })
          },
          {
            id: '3',
            title: t('INTERNAL_CONTROLS'),
            canDrop: false,
            items: [...controls]
              .sort((a, b) =>
                (a.control_name || '').localeCompare(b.control_name || '')
              )
              .map((c: any) => ({
                id: c.id,
                text: c.control_name ?? '',
                description: c.control_description || '',
                control_title: c.control_name || c.control_description || '',
                type: 'OTHER' as const,
                notUsed: c.not_used === true,
                owner_maintainer: c.owner_maintainer,
                control_status:
                  typeof c.control_status === 'string'
                    ? c.control_status
                    : c.control_status?.name ?? null
              }))
          }
        ];

        setColumns(newColumns);

        setAssignments(normalizedAssignments);
        setInitialAssignments(normalizedAssignments);
        setProcessesFromApi(processes);
        setRisksFromApi(risks);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Failed to fetch BPR data:', error);
      }
    };

    fetchData();
  }, [drawTable, selectedEntityId, dispatch, scopeOfFilters]);

  useEffect(() => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        title:
          col.id === '1'
            ? t('BUSINESS_PROCESSES')
            : col.id === '2'
            ? t('RISKS')
            : t('INTERNAL_CONTROLS')
      }))
    );
  }, [t]);

  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    const hasChanges =
      JSON.stringify(assignments) !== JSON.stringify(initialAssignments);
    dispatch(internalControlsOp.setHasChanges(hasChanges));
  }, [assignments, initialAssignments, dispatch]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  }, [assignments]);

  const handleDropRisk = useCallback(
    async (bprId: number, dragged: DragItem) => {
      if (dragged.itemType !== 'RISK') return;

      setAssignments((prev) => {
        const exists = prev.some(
          (a) =>
            a.bprId === bprId &&
            a.sourceType === 'RISK' &&
            a.sourceId === dragged.id
        );

        if (exists) {
          notification.info({
            message: 'Risk already added',
            description: 'This risk is already linked to the selected process.',
            placement: 'topRight',
            duration: 20
          });
          return prev;
        }

        const riskInstanceId = `risk-${dragged.id}-${Date.now()}`;

        return [
          ...prev,
          {
            assignmentId: `${bprId}-RISK-${dragged.id}-${Date.now()}`,
            bprId,
            riskInstanceId,
            sourceType: 'RISK' as const,
            sourceId: dragged.id,
            text: dragged.text,
            description: dragged.description,
            severity: dragged.severity
              ? {
                  id: dragged.severity.id,
                  name: dragged.severity.name as SeverityName
                }
              : null,
            control_status: null
          }
        ];
      });
    },
    []
  );

  const handleDropControl = useCallback(
    async (bprId: number, riskInstanceId: string | null, dragged: DragItem) => {
      if (dragged.itemType !== 'CONTROL' || !riskInstanceId) return;

      setAssignments((prev) => {
        const alreadyLinked = prev.some(
          (a) =>
            a.riskInstanceId === riskInstanceId &&
            a.sourceType === 'CONTROL' &&
            a.sourceId === dragged.id
        );

        if (alreadyLinked) {
          notification.info({
            message: 'Control already added',
            description: 'This control is already linked to the selected risk.',
            placement: 'topRight',
            duration: 20
          });
          return prev;
        }

        return [
          ...prev,
          {
            assignmentId: `${bprId}-CONTROL-${dragged.id}-${Date.now()}`,
            bprId,
            riskInstanceId,
            sourceType: 'CONTROL' as const,
            sourceId: dragged.id,
            text: dragged.text,
            description: dragged.description,
            control_status: dragged.control_status ?? null
          }
        ];
      });
    },
    []
  );

  const handleDeleteAssignment = useCallback(
    (assignmentId: string) => {
      setAssignments((prev) => {
        const found = prev.find((a) => a.assignmentId === assignmentId);
        if (!found) return prev;

        setHistory((h) => [...h, { columns, assignments: prev }]);

        return prev.filter((a) => a.assignmentId !== assignmentId);
      });
    },
    [columns]
  );

  const buildProcessRiskAllocations = (
    allAssignments: Assignment[],
    processes: any[],
    scope: Scope
  ): AllocateProcessRiskRequest[] => {
    return processes.map((process) => {
      const risksForProcess = allAssignments.filter(
        (a) => a.sourceType === 'RISK' && a.bprId === process.id && a.sourceId
      );

      const riskIds = Array.from(
        new Set(risksForProcess.map((r) => r.sourceId))
      );

      return {
        business_process_l4_id: process.id,
        scope,
        start_date: '2026-01-01',
        version: 1,
        comments: 'Initial allocation',
        owner_maintainer: 'risk_team',
        risk_ids: riskIds
      };
    });
  };

  const buildProcessRiskControlAllocations = (
    allAssignments: Assignment[],
    processes: any[],
    scope: Scope
  ): AllocateProcessRiskControlRequest[] => {
    const allocations: AllocateProcessRiskControlRequest[] = [];

    processes.forEach((process) => {
      const risksForProcess = allAssignments.filter(
        (a) =>
          a.sourceType === 'RISK' && a.bprId === process.id && a.riskInstanceId
      );

      risksForProcess.forEach((risk) => {
        const controlIds = allAssignments
          .filter(
            (a) =>
              a.sourceType === 'CONTROL' &&
              a.riskInstanceId === risk.riskInstanceId &&
              a.sourceId
          )
          .map((c) => c.sourceId);

        const uniqueControlIds = Array.from(new Set(controlIds));

        allocations.push({
          business_process_l4_id: process.id,
          risk_maintenance_id: risk.sourceId,
          scope,
          start_date: '2026-01-01',
          version: 1,
          comments: 'Control mapping update',
          owner_maintainer: 'risk_team',
          control_ids: uniqueControlIds
        });
      });
    });

    return allocations;
  };

  const handleSave = async (passedQuery?: Record<string, any>) => {
    const actualQuery = passedQuery || query;

    try {
      const hasChanges =
        JSON.stringify(assignments) !== JSON.stringify(initialAssignments);

      if (!hasChanges) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight'
        });
        return;
      }

      const scope = Helpers.buildScopeFromFilters(actualQuery);

      const hasScope =
        scope.group_id ||
        scope.division_id ||
        scope.region_id ||
        scope.legal_entity_id ||
        scope.business_unit_id;

      if (!hasScope) {
        notification.warning({
          message: 'Scope Required',
          description: 'Please select one scope filter before saving.',
          placement: 'topRight'
        });
        return;
      }

      const processRiskPayloads = buildProcessRiskAllocations(
        assignments,
        processesFromApi,
        scope
      );

      const processRiskControlPayloads = buildProcessRiskControlAllocations(
        assignments,
        processesFromApi,
        scope
      );

      const riskResponses: { data?: { message?: string } }[] = [];
      const controlResponses: { data?: { message?: string } }[] = [];

      for (const payload of processRiskPayloads) {
        const res = await ApiService.allocateProcessRisk(payload);
        riskResponses.push(res);
      }

      for (const payload of processRiskControlPayloads) {
        const res = await ApiService.allocateProcessRiskControl(payload);
        controlResponses.push(res);
      }

      const lastMessage =
        controlResponses.at(-1)?.data?.message ||
        riskResponses.at(-1)?.data?.message ||
        'Process, risk, and control allocations saved successfully.';

      notification.success({
        message: 'Changes saved',
        description: lastMessage,
        placement: 'topRight'
      });

      setInitialAssignments(assignments);
      dispatch(internalControlsOp.setHasChanges(false));
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      console.error('Save failed', error);
      notification.error({
        message: 'Save failed',
        description:
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while saving. Please try again.',
        placement: 'topRight'
      });
    }
  };

  return (
    <InternalControlLayout onSave={handleSave}>
      <DndProvider backend={backend || HTML5Backend}>
        <Styled.Wrapper scheme={scheme}>
          <Styled.DndHintBanner scheme={scheme}>
            <Styled.DndHintTitle scheme={scheme}>
              {t('DND_HINT_TITLE')}
            </Styled.DndHintTitle>
            <Styled.DndHintDesc scheme={scheme}>
              {t('DND_HINT_DESC')}
            </Styled.DndHintDesc>
          </Styled.DndHintBanner>
          <Styled.DropBox>
            {columns.map((col) => (
              <ColumnView
                key={col.id}
                col={col}
                assignments={assignments}
                onDropRisk={handleDropRisk}
                onDropControl={handleDropControl}
                onDeleteAssignment={handleDeleteAssignment}
              />
            ))}
          </Styled.DropBox>
        </Styled.Wrapper>
      </DndProvider>
    </InternalControlLayout>
  );
};

export default DragAndDropBPR;
